"use client";

import React, { useCallback } from "react";
import { Sun, Moon, Monitor } from "lucide-react";

import { cn } from "@/lib/utils";

type ColorMode = "light" | "dark" | "system";

interface ColorModeSelectorProps {
  value: ColorMode;
  onChange: (mode: ColorMode) => void;
}

const MODE_OPTIONS: { key: ColorMode; label: string; icon: typeof Sun }[] = [
  { key: "light", label: "Light", icon: Sun },
  { key: "dark", label: "Dark", icon: Moon },
  { key: "system", label: "System", icon: Monitor },
];

export const ColorModeSelector = React.memo(function ColorModeSelector({
  value,
  onChange,
}: ColorModeSelectorProps): React.ReactElement {
  const handleClick = useCallback(
    (mode: ColorMode) => {
      onChange(mode);
    },
    [onChange],
  );

  return (
    <div className="flex gap-2">
      {MODE_OPTIONS.map(({ key, label, icon: Icon }) => {
        const isSelected = value === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => handleClick(key)}
            className={cn(
              "flex items-center gap-2 rounded-lg border px-4 py-3 text-sm font-medium transition-all",
              isSelected
                ? "border-primary bg-primary/10 text-primary"
                : "border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground",
            )}
          >
            <Icon size={16} />
            {label}
          </button>
        );
      })}
    </div>
  );
});
